import { useId, useMemo, useRef, useState } from 'react';
import { albumTagChange, type AlbumCard, type PhotoCard } from '@photos/shared';
import { useAlbums, useCreateAlbum } from '../api/hooks';
import { useOutsideClick } from '../hooks/useOutsideClick';
import { fmtCount } from '../lib/format';
import { useToast } from '../state/toasts';
import { Icon } from './Icon';
import { Modal } from './Modal';
import { TagChip } from './TagChip';

/** The chosen album: an existing one, or a new one to create with this name. */
export type AlbumValue = { id: string; name: string } | { newName: string } | null;

const RECENT = 8;

interface AlbumComboboxProps {
  albums: Pick<AlbumCard, 'id' | 'name' | 'updatedAt' | 'total'>[];
  value: AlbumValue;
  onChange(value: AlbumValue): void;
  /** offer "+ New album: <typed name>" */
  allowCreate: boolean;
  /** albums that can't be chosen (e.g. the current one when moving) */
  excludeIds?: readonly string[];
  disabled?: boolean;
  id?: string;
  placeholder?: string;
}

/**
 * Search over albums with the recently changed ones first, plus "+ New album" for instant creation. The chosen album
 * shows as the input text; typing again starts a new search.
 */
export function AlbumCombobox({ albums, value, onChange, allowCreate, excludeIds = [], disabled, id, placeholder }: AlbumComboboxProps) {
  const listId = useId();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const refs = useRef([wrap]).current;
  useOutsideClick(refs, () => setOpen(false), open);

  const typed = query.trim();
  const options = useMemo(() => {
    const q = typed.toLowerCase();
    const list = albums
      .filter((a) => !excludeIds.includes(a.id) && (!q || a.name.toLowerCase().includes(q)))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    const shown: ({ kind: 'album'; album: (typeof albums)[number] } | { kind: 'new'; name: string })[] = (q ? list : list.slice(0, RECENT)).map(
      (album) => ({ kind: 'album' as const, album }),
    );
    if (allowCreate && typed && !albums.some((a) => a.name.toLowerCase() === q)) shown.push({ kind: 'new', name: typed });
    return shown;
  }, [albums, typed, allowCreate, excludeIds]);

  const choose = (i: number) => {
    const o = options[i];
    if (!o) return;
    onChange(o.kind === 'album' ? { id: o.album.id, name: o.album.name } : { newName: o.name });
    setQuery('');
    setOpen(false);
  };

  const label = value ? ('id' in value ? value.name : `New album: ${value.newName}`) : '';

  return (
    <div className="albumbox" ref={wrap}>
      <div className="search">
        <Icon name="folder" style={{ color: 'var(--muted)' }} />
        <input
          id={id}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          placeholder={placeholder ?? 'Search albums or type a new name…'}
          value={open ? query : label || query}
          onFocus={() => {
            setOpen(true);
            setHi(0);
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHi(0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
              e.preventDefault();
              setOpen(true);
              if (options.length) setHi((h) => (h + (e.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length);
            } else if (e.key === 'Enter') {
              e.preventDefault();
              choose(hi);
            } else if (e.key === 'Escape' && open) {
              e.preventDefault();
              e.stopPropagation();
              setOpen(false);
            }
          }}
        />
      </div>
      <div className={`ac${open ? ' open' : ''}`} id={listId} role="listbox">
        {!typed && options.length > 0 && <div className="hint">Recent albums</div>}
        {!options.length && <div className="hint">{allowCreate ? 'Type a name to create an album.' : 'No matching albums.'}</div>}
        {options.map((o, i) => (
          <div
            key={o.kind === 'album' ? o.album.id : 'new'}
            role="option"
            aria-selected={i === hi}
            className={`row${i === hi ? ' hi' : ''}`}
            onMouseDown={(e) => {
              e.preventDefault();
              choose(i);
            }}
          >
            {o.kind === 'album' ? (
              <>
                <Icon name="folder" style={{ color: 'var(--muted)' }} />
                <span>{o.album.name}</span>
                <span className="n">{fmtCount(o.album.total)} photos</span>
              </>
            ) : (
              <>
                <Icon name="upload" style={{ color: 'var(--accent)' }} />
                <b style={{ color: 'var(--accent)' }}>+ New album: “{o.name}”</b>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** Per tag, how many of the photos gain or lose it. */
type TagDiff = { add: [string, number][]; remove: [string, number][] };

/** The album-default tag changes a move ("Update tags") or an add ("Also add this album's tags") would make. */
export function albumTagDiff(
  photos: readonly Pick<PhotoCard, 'tags' | 'homeAlbumId'>[],
  defaultsOf: (albumId: string | undefined) => readonly string[],
  target: readonly string[],
  mode: 'move' | 'add',
): TagDiff {
  const add = new Map<string, number>();
  const remove = new Map<string, number>();
  for (const p of photos) {
    const d = albumTagChange(p.tags, mode === 'move' ? defaultsOf(p.homeAlbumId) : [], target);
    for (const t of d.add) add.set(t, (add.get(t) ?? 0) + 1);
    for (const t of d.remove) remove.set(t, (remove.get(t) ?? 0) + 1);
  }
  return { add: [...add], remove: [...remove] };
}

interface AlbumPickerDialogProps {
  title: string;
  help: string;
  confirmLabel: string;
  excludeIds?: readonly string[];
  /** offer "+ New album" (needs media) */
  allowCreate: boolean;
  busy?: boolean;
  /**
   * Move: "Update tags" (checked by default); add: "Also add this album's tags" (unchecked). Shown only once an album
   * is chosen and only when its default tags would change something, with the exact diff for these photos.
   */
  tags?: { mode: 'move' | 'add'; photos: readonly Pick<PhotoCard, 'tags' | 'homeAlbumId'>[] };
  /** Add only: "Make a separate copy" (default) or "Show in both albums" (the same photo); the button follows the choice. */
  copyChoice?: boolean;
  onPick(album: { id: string; name: string }, applyTagChanges: boolean, asCopy: boolean): void;
  onClose(): void;
}

/** "Move to album…", "Add to album…" and "Add to Glean": choose an album (or create one inline). */
export function AlbumPickerDialog({ title, help, confirmLabel, excludeIds, allowCreate, busy, tags, copyChoice, onPick, onClose }: AlbumPickerDialogProps) {
  const albums = useAlbums();
  const create = useCreateAlbum();
  const toast = useToast();
  const [value, setValue] = useState<AlbumValue>(null);
  const [applyTags, setApplyTags] = useState(tags?.mode === 'move');
  const [asCopy, setAsCopy] = useState(Boolean(copyChoice));
  const list = albums.data?.items ?? [];
  const defaultsOf = (id: string | undefined) => list.find((a) => a.id === id)?.defaultTags ?? [];
  const target = value && 'id' in value ? defaultsOf(value.id) : [];
  const diff = tags && value ? albumTagDiff(tags.photos, defaultsOf, target, tags.mode) : null;
  const tagsChange = !!diff && (diff.add.length > 0 || diff.remove.length > 0);
  const n = tags?.photos.length ?? 0;
  const count = (k: number) => (n > 1 ? <span className="auto"> {k === n ? 'all' : k}</span> : undefined);

  const confirm = () => {
    if (!value) return;
    const apply = tagsChange && applyTags;
    if ('id' in value) return onPick(value, apply, asCopy);
    create.mutate(
      { name: value.newName },
      {
        onSuccess: (a) => onPick({ id: a.id, name: a.name }, apply, asCopy),
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t create the album', sub: e.message }),
      },
    );
  };

  return (
    <Modal onClose={onClose} labelledBy="album-pick-title">
      <h3 id="album-pick-title">{title}</h3>
      <p className="help">{help}</p>
      <div className="field">
        <AlbumCombobox albums={list} value={value} onChange={setValue} allowCreate={allowCreate} excludeIds={excludeIds} />
      </div>
      {copyChoice && (
        <fieldset className="field choice" aria-label="How to add">
          <label className="toggle">
            <input type="radio" name="add-how" checked={asCopy} onChange={() => setAsCopy(true)} />
            <span>
              <b>Make a separate copy</b>
              <small>A new, independent photo in this album. Changes to one don’t affect the other.</small>
            </span>
          </label>
          <label className="toggle">
            <input type="radio" name="add-how" checked={!asCopy} onChange={() => setAsCopy(false)} />
            <span>
              <b>Show in both albums</b>
              <small>The same photo appears in both albums. Changes like tags, release or trash apply everywhere.</small>
            </span>
          </label>
        </fieldset>
      )}
      {tags && diff && tagsChange && (
        <div className="field tagdiff">
          <label className="toggle">
            <input type="checkbox" checked={applyTags} onChange={(e) => setApplyTags(e.target.checked)} />
            {tags.mode === 'move' ? 'Update tags' : 'Also add this album’s tags'}
          </label>
          <div className={`diff${applyTags ? '' : ' off'}`} aria-label="Tag changes">
            {diff.remove.map(([t, k]) => (
              <span key={`-${t}`} className="minus">
                − <TagChip tag={t} extra={count(k)} />
              </span>
            ))}
            {diff.add.map(([t, k]) => (
              <span key={`+${t}`} className="plus">
                + <TagChip tag={t} extra={count(k)} />
              </span>
            ))}
          </div>
          <p className="help" style={{ margin: '6px 0 0' }}>
            Only album tags are touched, never tags added by hand.
          </p>
        </div>
      )}
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary" disabled={!value || busy || create.isPending} onClick={confirm}>
          {copyChoice ? (asCopy ? 'Copy to album' : 'Add to album') : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
